import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchAttorneyMessagingStatus } from '../../services/Feed.service';
import { checkUserRoleType } from '../../mappers/feedUtility';
import { ROLES, getJanusToken, setJanusToken } from '../../../../../constants';
import { fetchJanusToken } from '../../../../../util/APIUtils';
import { getActiveLocationId } from '../../../../services/utilty';

/**
 * Hook to manage attorney messaging feature status and visibility
 * Handles fetching attorney messaging status, checking user role permissions,
 * and managing attorney modal state
 * Attorneys cannot see the link. Only Super Admin or Staff roles can see it (with API check)
 * @param {Function} onCloseNewMessageModal - Callback to close the NewMessageModal when opening AttorneyModal
 * @returns {Object} Object containing canShowAttorneyLink, showAttorneyModal, handleOpenAttorneyModal, handleCloseAttorneyModal
 */
export const useAttorneyMessagingHook = (onCloseNewMessageModal) => {
  const [isAttorneyMessagingEnabled, setIsAttorneyMessagingEnabled] = useState(false);
  const [showAttorneyModal, setShowAttorneyModal] = useState(false);

  // Check if user is Attorney (attorneys should not see the link)
  const isAttorney = useMemo(() => {
    return checkUserRoleType([ROLES.ATTORNEY]);
  }, []);

  // Function to check attorney messaging status
  const checkAttorneyMessagingStatus = async () => {
    try {
      // Check if janus token is available
      let janusToken = getJanusToken();
      
      // If token is missing, fetch it first
      if (!janusToken) {
        try {
          const activeLocationId = getActiveLocationId();
          if (activeLocationId) {
            const janusTokenResponse = await fetchJanusToken(activeLocationId);
            if (janusTokenResponse?.token) {
              setJanusToken(janusTokenResponse.token);
              janusToken = janusTokenResponse.token;
            }
          } else {
            console.warn('[useAttorneyMessagingHook] Active location ID not available, cannot fetch janus token');
          }
        } catch (tokenError) {
          console.error('[useAttorneyMessagingHook] Failed to fetch janus token:', tokenError);
          // Continue with status fetch even if token fetch fails
          // The API call will handle the missing token scenario
        }
      }

      // Proceed with fetching attorney messaging status
      const status = await fetchAttorneyMessagingStatus();
      setIsAttorneyMessagingEnabled(status);
    } catch (error) {
      console.error('[useAttorneyMessagingHook] Failed to fetch attorney messaging status:', error);
      // Set to false on error to prevent showing the link
      setIsAttorneyMessagingEnabled(false);
    }
  };

  // Fetch messagingAttorney status from /setting/feature/ API only if user is not an attorney
  useEffect(() => {
    // Skip API call if user is an attorney
    if (isAttorney) {
      return;
    }

    checkAttorneyMessagingStatus();
  }, [isAttorney]);

  // Check if user is Super Admin or Staff (attorneys cannot see the link)
  const canShowAttorneyLink = useMemo(() => {
    // Attorneys should not see the link
    if (isAttorney) {
      return false;
    }

    // For non-attorneys, check Super Admin or Staff roles and API status
    const rolesToCheck = [ROLES.SUPER_ADMIN, ROLES.STAFF];
    const hasRole = checkUserRoleType(rolesToCheck);
    return isAttorneyMessagingEnabled && hasRole;
  }, [isAttorney, isAttorneyMessagingEnabled]);

  // Handle opening attorney modal
  const handleOpenAttorneyModal = useCallback(() => {
    // Close the NewMessageModal first if callback is provided
    if (onCloseNewMessageModal) {
      onCloseNewMessageModal();
    }
    // Then open the AttorneyModal
    setShowAttorneyModal(true);
  }, [onCloseNewMessageModal]);

  // Handle closing attorney modal
  const handleCloseAttorneyModal = useCallback(() => {
    setShowAttorneyModal(false);
  }, []);

  return {
    canShowAttorneyLink,
    showAttorneyModal,
    handleOpenAttorneyModal,
    handleCloseAttorneyModal,
  };
};

